import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { supabase } from '../lib/supabase';
import Icon from './AppIcon';

// NOVI PROP: currentAdminAziendaId — id_azienda ulogovanog admina, proslijeđen
// iz roditeljske komponente (RichiesteFerieDipendenti -> FerieAdminPage).
// Koristi se kao dodatni ('.eq('id_azienda', ...)') uslov na SVAKOM
// update/delete pozivu — defense-in-depth POVRH RLS politika na bazi.
// RLS je krajnja linija odbrane; ova provjera sprječava da UI ikad ni
// POKUŠA slati zahtjev za red koji ne pripada trenutnoj firmi, umjesto da
// se osloni isključivo na to da baza odbije zahtjev.
const RichiestaFerieModalAdmin = ({ isOpen, richiesta, currentAdminAziendaId, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (richiesta) {
      setFormData({
        dal: richiesta.dal || '',
        al: richiesta.al || '',
        ore_dal: richiesta.ore_dal || '',
        ore_al: richiesta.ore_al || '',
        giorni: richiesta.giorni || 0,
        turno: richiesta.turno || 'Giornaliero',
        note: richiesta.note || '',
        weekend_incluso: richiesta.weekend_incluso || false,
      });
      setEditMode(false);
      setConfirmDelete(false);
      setActionError('');
    }
  }, [richiesta]);

  if (!isOpen || !richiesta || !formData) return null;

  const getAdminName = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: profilo } = await supabase
      .from('profili')
      .select('nome, cognome')
      .eq('id', user.id)
      .single();
    return profilo ? `${profilo.nome} ${profilo.cognome}` : 'Admin';
  };

  // Provjera koja se radi PRIJE svake write operacije: ako po bilo kom razlogu
  // nemamo pouzdan currentAdminAziendaId, blokiramo akciju umjesto da pošaljemo
  // upit koji bi mogao pogrešno pogoditi (ili RLS blokirati bez objašnjenja).
  const guardCompanyMatch = () => {
    if (!currentAdminAziendaId) {
      setActionError(t('ferie.admin.errors.no_company', { defaultValue: 'Impossibile verificare l\'azienda. Ricarica la pagina.' }));
      return false;
    }
    if (richiesta.id_azienda && richiesta.id_azienda !== currentAdminAziendaId) {
      setActionError(t('ferie.admin.errors.company_mismatch', { defaultValue: 'Questa richiesta non appartiene alla tua azienda.' }));
      return false;
    }
    return true;
  };

  const handleDecision = async (accettato) => {
    if (!guardCompanyMatch()) return;
    setProcessing(true);
    setActionError('');
    try {
      const adminName = await getAdminName();
      const { error } = await supabase
        .from('ferie')
        .update({
          accettato,
          risposto_da: adminName,
          data_risposta: new Date().toISOString(),
        })
        .eq('id', richiesta.id)
        .eq('id_azienda', currentAdminAziendaId);

      if (error) throw error;
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      console.error('Errore aggiornamento:', err);
      setActionError(t('ferie.admin.errors.update_failed', { defaultValue: 'Aggiornamento non riuscito.' }));
    }
    setProcessing(false);
  };

  const handleSaveEdit = async () => {
    if (!guardCompanyMatch()) return;
    setProcessing(true);
    setActionError('');
    try {
      const { error } = await supabase
        .from('ferie')
        .update({
          dal: formData.dal,
          al: formData.al,
          ore_dal: formData.ore_dal || null,
          ore_al: formData.ore_al || null,
          giorni: formData.giorni,
          turno: formData.turno,
          note: formData.note || null,
          weekend_incluso: formData.weekend_incluso,
        })
        .eq('id', richiesta.id)
        .eq('id_azienda', currentAdminAziendaId);

      if (error) throw error;
      if (onUpdated) onUpdated();
      setEditMode(false);
      onClose();
    } catch (err) {
      console.error('Errore modifica:', err);
      setActionError(t('ferie.admin.errors.update_failed', { defaultValue: 'Aggiornamento non riuscito.' }));
    }
    setProcessing(false);
  };

  const handleDelete = async () => {
    if (!guardCompanyMatch()) return;
    setProcessing(true);
    setActionError('');
    try {
      const { error } = await supabase
        .from('ferie')
        .delete()
        .eq('id', richiesta.id)
        .eq('id_azienda', currentAdminAziendaId);

      if (error) throw error;
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      console.error('Errore eliminazione:', err);
      setActionError(t('ferie.admin.errors.delete_failed', { defaultValue: 'Eliminazione non riuscita.' }));
    }
    setProcessing(false);
  };

  const formatTipo = (tipo) => {
    if (tipo === 'giorno_singolo') return t('ferie.type_single_day');
    if (tipo === 'piu_giorni') return t('ferie.type_multi_day');
    if (tipo === 'ore') return t('ferie.type_hours');
    return tipo;
  };

  const formatDateTime = (dt) => {
    if (!dt) return '—';
    const locale = i18n.language === 'bs' ? 'bs-BA' : i18n.language === 'it' ? 'it-IT' : i18n.language === 'de' ? 'de-DE' : 'en-GB';
    return new Date(dt).toLocaleString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const StatusBadge = ({ accettato }) => {
    if (accettato === true) {
      return (
        <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-[9px] font-black uppercase tracking-widest border border-emerald-500/20">
          {t('ferie.status_accepted')}
        </span>
      );
    }
    if (accettato === false) {
      return (
        <span className="px-3 py-1 rounded-full bg-destructive/10 text-destructive text-[9px] font-black uppercase tracking-widest border border-destructive/20">
          {t('ferie.status_rejected')}
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full bg-orange-500/10 text-orange-500 text-[9px] font-black uppercase tracking-widest border border-orange-500/20">
        {t('ferie.status_pending')}
      </span>
    );
  };

  // POPRAVKA BUGA: komponenta je ranije čitala samo 'richiesta.data_richiesta',
  // ali tabela/insert payload koriste 'created_at' — 'data_richiesta' nikad
  // nije postojao kao kolona, pa je timeline uvijek prikazivao '—'. Fallback
  // na oba naziva rješava to bez potrebe za izmjenom šeme baze.
  const dataRichiestaValue = richiesta.data_richiesta || richiesta.created_at;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[3px] px-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-card rounded-[2rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-border/50 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6 pb-5 border-b border-border/60 bg-gradient-to-b from-muted/30 to-transparent shrink-0">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 hover:bg-muted rounded-xl transition-colors text-muted-foreground"
          >
            <Icon name="X" size={18} />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 text-primary rounded-2xl">
              <Icon name="User" size={22} />
            </div>
            <div>
              <h2 className="font-black uppercase text-[13px] tracking-widest text-foreground">
                {richiesta.nome} {richiesta.cognome}
              </h2>
              <p className="text-[10px] font-bold text-muted-foreground tracking-wide mt-0.5">
                {richiesta.email}{richiesta.phone ? ` · ${richiesta.phone}` : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex justify-center mb-5">
            <StatusBadge accettato={richiesta.accettato} />
          </div>

          {actionError && (
            <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-2xl">
              <p className="text-red-500 text-[11px] text-center font-bold uppercase">{actionError}</p>
            </div>
          )}

          {!editMode ? (
            <div className="space-y-1">
              {[
                { label: t('ferie.type_label'), value: formatTipo(richiesta.tipo_richiesta), icon: 'Tag' },
                { label: t('ferie.from_label'), value: formData.dal || '—', icon: 'Calendar' },
                { label: t('ferie.to_label'), value: formData.al || '—', icon: 'Calendar' },
                ...(formData.ore_dal ? [{ label: t('ferie.time_label'), value: `${formData.ore_dal} - ${formData.ore_al}`, icon: 'Clock' }] : []),
                { label: t('ferie.days_column_label'), value: formData.giorni > 0 ? formData.giorni : '—', icon: 'Hash' },
                { label: t('ferie.shift_label'), value: formData.turno || '—', icon: 'Sunrise' },
                { label: t('ferie.weekend_included'), value: formData.weekend_incluso ? t('ferie.yes') : t('ferie.no'), icon: 'CalendarDays' },
                { label: t('ferie.note_label'), value: formData.note || '—', icon: 'StickyNote' },
              ].map(({ label, value, icon }) => (
                <div key={label} className="flex justify-between items-center py-2.5 border-b border-border/30">
                  <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                    <Icon name={icon} size={12} className="text-muted-foreground/50" />
                    {label}
                  </span>
                  <span className="text-[12px] font-bold text-foreground text-right max-w-[55%] truncate">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                    {t('ferie.from_label')}
                  </label>
                  <input
                    type="date"
                    value={formData.dal}
                    onChange={(e) => setFormData({ ...formData, dal: e.target.value })}
                    className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                    {t('ferie.to_label')}
                  </label>
                  <input
                    type="date"
                    value={formData.al}
                    onChange={(e) => setFormData({ ...formData, al: e.target.value })}
                    className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                  />
                </div>
              </div>

              {richiesta.tipo_richiesta === 'ore' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                      {t('ferie.from_time_label')}
                    </label>
                    <input
                      type="time"
                      value={formData.ore_dal}
                      onChange={(e) => setFormData({ ...formData, ore_dal: e.target.value })}
                      className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                      {t('ferie.to_time_label')}
                    </label>
                    <input
                      type="time"
                      value={formData.ore_al}
                      onChange={(e) => setFormData({ ...formData, ore_al: e.target.value })}
                      className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                  {t('ferie.days_column_label')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.giorni}
                  onChange={(e) => setFormData({ ...formData, giorni: parseInt(e.target.value) || 0 })}
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                  {t('ferie.shift_label')}
                </label>
                <select
                  value={formData.turno}
                  onChange={(e) => setFormData({ ...formData, turno: e.target.value })}
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                >
                  <option value="Giornaliero">{t('ferie.shift_daily')}</option>
                  <option value="Primo Turno">{t('ferie.shift_first')}</option>
                  <option value="Secondo Turno">{t('ferie.shift_second')}</option>
                  <option value="Terzo Turno">{t('ferie.shift_third')}</option>
                  <option value="Altro">{t('ferie.shift_other')}</option>
                </select>
              </div>

              <label className="flex items-center gap-3 cursor-pointer bg-muted/20 p-4 rounded-xl border border-border/40">
                <input
                  type="checkbox"
                  checked={formData.weekend_incluso}
                  onChange={(e) => setFormData({ ...formData, weekend_incluso: e.target.checked })}
                  className="w-5 h-5 accent-primary"
                />
                <span className="text-[11px] font-black uppercase tracking-widest text-foreground">
                  {t('ferie.weekend_included')}
                </span>
              </label>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                  {t('ferie.note_label')}
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={3}
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10 resize-none"
                />
              </div>
            </div>
          )}

          <div className="mt-4 rounded-2xl bg-muted/30 border border-border/40 p-4 space-y-2.5">
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 mb-1">
              {t('ferie.timeline_title')}
            </p>
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-muted-foreground">{t('ferie.timeline_sent')}</span>
              <span className="text-[11px] font-black text-foreground">{formatDateTime(dataRichiestaValue)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-muted-foreground">{t('ferie.timeline_answered_by')}</span>
              <span className="text-[11px] font-black text-foreground">{richiesta.risposto_da || '—'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-muted-foreground">{t('ferie.timeline_answer_date')}</span>
              <span className="text-[11px] font-black text-foreground">
                {richiesta.data_risposta ? formatDateTime(richiesta.data_risposta) : t('ferie.status_pending')}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 pt-4 border-t border-border/40 shrink-0 space-y-3">
          {!confirmDelete ? (
            <>
              {editMode ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => setEditMode(false)}
                    disabled={processing}
                    className="flex-1 py-3 rounded-2xl bg-muted text-muted-foreground font-black uppercase text-[10px] tracking-widest hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    {t('ferie.cancel_btn')}
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-primary text-white font-black uppercase text-[10px] tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {processing ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Icon name="Check" size={14} /> {t('ferie.admin.save_changes')}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleDecision(false)}
                      disabled={processing}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-destructive/10 text-destructive font-black uppercase text-[10px] tracking-widest hover:bg-destructive/20 transition-all disabled:opacity-50"
                    >
                      <Icon name="X" size={14} /> {t('ferie.admin.reject_btn')}
                    </button>
                    <button
                      onClick={() => handleDecision(true)}
                      disabled={processing}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-emerald-500 text-white font-black uppercase text-[10px] tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      <Icon name="Check" size={14} /> {t('ferie.admin.approve_btn')}
                    </button>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setEditMode(true)}
                      disabled={processing}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-muted text-foreground font-black uppercase text-[10px] tracking-widest hover:bg-muted/70 transition-all disabled:opacity-50"
                    >
                      <Icon name="Pencil" size={14} /> {t('ferie.admin.edit_btn')}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(true)}
                      disabled={processing}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-destructive/10 text-destructive font-black uppercase text-[10px] tracking-widest hover:bg-destructive/20 transition-all disabled:opacity-50"
                    >
                      <Icon name="Trash2" size={14} /> {t('ferie.delete_btn')}
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-muted-foreground text-center uppercase tracking-wide">
                {t('ferie.delete_confirm_text')}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={processing}
                  className="flex-1 py-3 rounded-2xl bg-muted text-muted-foreground font-black uppercase text-[10px] tracking-widest hover:text-foreground transition-colors disabled:opacity-50"
                >
                  {t('ferie.cancel_btn')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-destructive text-white font-black uppercase text-[10px] tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {processing ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Icon name="Trash2" size={14} />
                      {t('ferie.confirm_delete_btn')}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RichiestaFerieModalAdmin;